import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import AppleSignInButton from "@/components/AppleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(location.state?.from || "/dashboard");
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Auto-launch Google OAuth if the user was bounced here from a
  // non-canonical bidorah.com host — handled globally inside
  // GoogleSignInButton so Login, Register, and Landing all work.

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="login-page">
      <Navbar />
      <main className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:block relative bg-zinc-900 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1770321428577-4446e5cb7bab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJsdWUlMjBnZW9tZXRyaWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3Njg1ODIzM3ww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="h-full w-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute bottom-12 left-12 right-12 text-white">
            <p className="font-display font-black text-4xl tracking-tighter leading-tight">
              Real-time auctions.<br />Verified sellers.<br />Zero crossover.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Welcome back</h1>
            <p className="text-zinc-600 mb-8">
              New to Bidorah?{" "}
              <Link to="/register" className="text-[#1D78AB] font-semibold" data-testid="link-register">
                Create an account
              </Link>
            </p>

            <form onSubmit={handle} className="space-y-4" data-testid="login-form">
              <div>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Email"
                  className="h-11"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Password"
                    className="h-11 pr-12"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-full text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeSlashIcon size={18} weight="bold" /> : <EyeIcon size={18} weight="bold" />}
                  </button>
                </div>
                <div className="text-right mt-2">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-[#1D78AB] hover:underline font-semibold"
                    data-testid="login-forgot-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
              {err && (
                <p className="text-sm text-red-600" data-testid="login-error">{err}</p>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1D78AB] hover:bg-[#165d85] text-white h-11"
                data-testid="login-submit-btn"
                id="login-submit-button"
              >
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </form>

            <div className="mt-5 flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-zinc-200" />
              <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">or</span>
              <span className="h-px flex-1 bg-zinc-200" />
            </div>

            <div className="mt-4 space-y-3">
              <GoogleSignInButton testid="login-google-btn" />
              <AppleSignInButton testid="login-apple-btn" />
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-200">
              <Link
                to="/why-bidorah"
                className="flex items-center justify-between gap-3 rounded-2xl bg-zinc-50 hover:bg-[#1D78AB]/5 border border-zinc-200 hover:border-[#1D78AB] transition-colors px-4 py-3 group"
                data-testid="login-why-bidorah-link"
              >
                <div>
                  <div className="text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                    New here?
                  </div>
                  <div className="text-sm font-semibold text-zinc-900 mt-0.5">
                    See what makes Bidorah different →
                  </div>
                </div>
                <span className="text-[#1D78AB] group-hover:translate-x-0.5 transition-transform text-xl font-black">
                  →
                </span>
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
